import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { apiKey } from "../../../config/config";
import axios from "axios";
import MessageCard from "../../../components/AdminCards/MessageCard";
import { FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import AddSupplierModal from "../../../components/Modal/AddSupplierModal";
import {
  generateReferenceNumber,
  languageHelper,
} from "../../../config/helperFunctions";

function AddPurchasesForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    reference_no: generateReferenceNumber(),
    supplier_id: "",
    date: new Date().toISOString().split("T")[0], // Set date as current date
    grand_total: "",
    paid: "",
    due: "",
    payment_method_id: "",
    note: "",
    user_id: user?.user.id,
    outlet_id: user?.user.outlet_id,
  });
  const [selectedIngredient, setSelectedIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const handleChange = (e) =>
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

  const handleIngredientSelect = (e) => {
    setSelectedIngredients([
      ...selectedIngredient,
      {
        ingredient_id: e.value,
        name: e.ingredient.name,
        code: e.ingredient.code,
        qty: "",
        price: e.ingredient.purchase_price,
        unit: e.ingredient.purchase_ingredient_unit?.unit_name || "",
        unitValue: e.ingredient.purchase_ingredient_unit?.unit_value || "",
        vat_percentage: e.ingredient.vat_percentage,
        tax_method: e.ingredient.tax_method,
        vat: e.ingredient.ing_vat,
        total: "",
      },
    ]);
  };

  const handleAddRow = () => {
    setSelectedIngredients([
      ...selectedIngredient,
      {
        isNew: true,
        name: "",
        code: "",
        qty: "",
        price: "",
        unit: "",
        unitValue: "",
        vat_percentage: "",
        tax_method: "",
        vat: "",
      },
    ]);
  };

  const handleIngredientValueChange = (e, index) => {
    const { name, value } = e.target;

    setSelectedIngredients((prevIngredients) =>
      prevIngredients.map((ingredient, idx) => {
        if (idx !== index) return ingredient; // Keep other ingredients unchanged

        // Extract the updated value and field name
        const updatedIngredient = { ...ingredient, [name]: value };

        // Parse numeric values for calculation
        const price = parseFloat(updatedIngredient.price) || 0;
        const qty = parseFloat(updatedIngredient.qty) || 0;
        const vatPercentage = parseFloat(updatedIngredient.vat_percentage) || 0;
        const taxMethod =
          updatedIngredient.tax_method?.toLowerCase() || "no tax";

        let vat = 0;
        let total = 0;

        // Calculate VAT and Total based on the tax method
        if (taxMethod === "inclusive") {
          vat = (price * vatPercentage) / (100 + vatPercentage);
          total = price * qty; // Inclusive means price already includes VAT
        } else if (taxMethod === "exclusive") {
          vat = (price * vatPercentage) / 100;
          total = (price + vat) * qty; // Exclusive means VAT is added to the price
        } else {
          total = price * qty; // No tax
        }

        // Update the calculated fields
        updatedIngredient.vat = vat.toFixed(2);
        updatedIngredient.total = total.toFixed(2);

        return updatedIngredient;
      })
    );
  };

  const handleDelete = (index) => {
    setSelectedIngredients((prevIngredients) =>
      prevIngredients.filter((_, i) => i !== index)
    );
  };

  const navigate = useNavigate();

  const fetchSuppliers = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/suppliers`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        const mappedSuppliers = res.data.map((sup) => ({
          label: sup.name,
          value: sup.id,
        }));
        setSuppliers(mappedSuppliers);
        setError(false);
      })
      .catch((err) => {
        setError(err.response?.data.message || "Error Occured");
      })
      .finally(() => {});
  }, [user?.token, user?.user.outlet_id]);

  const fetchIngredients = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/ingredients`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        const mappedIngredients = res.data.map((ing) => ({
          label: ing.name,
          value: ing.id,
          ingredient: ing,
        }));
        setIngredients(mappedIngredients);
        setError(false);
      })
      .catch((err) => {
        setError(err.response?.data.message || "Error Occured");
      })
      .finally(() => {});
  }, [user?.token, user?.user.outlet_id]);

  const fetchPayment = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/get-all-payment`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setPaymentMethods(
          res.data.map((method, index) => {
            // if (method.id >= 5) return;
            return {
              label: method.name,
              value: method.id,
              index,
            };
          })
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token]);

  const handleFetchUnits = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/ingredientUnits`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        setUnits(res.data);
      })
      .catch((err) => {
        setError(err.response?.data.message || "Error Occured");
      });
  }, [user?.token, user?.user.outlet_id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const formBody = {
      ...formData,
      ingredients: selectedIngredient,
    };

    if (id) {
      axios
        .put(`${apiKey}/api/rms/admin/purchase/${id}`, formBody, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      axios
        .post(`${apiKey}/api/rms/admin/purchase`, formBody, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchIngredients();
    handleFetchUnits();
    fetchPayment();
  }, [fetchIngredients, fetchPayment, fetchSuppliers, handleFetchUnits]);

  useEffect(() => {
    setFormData((prevData) => {
      let grandTotal = 0;
      selectedIngredient.forEach((ing) => {
        grandTotal += parseFloat(ing.total);
      });
      return {
        ...prevData,
        grand_total: parseFloat(grandTotal).toFixed(2),
      };
    });
  }, [selectedIngredient]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      due: parseFloat(formData.grand_total - formData.paid).toFixed(2),
    }));
  }, [formData.grand_total, formData.paid]);

  useEffect(() => {
    if (id) {
      setPageLoading(true);
      axios
        .get(`${apiKey}/api/rms/admin/purchase/${id}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          setFormData(res.data);
          setSelectedIngredients(JSON.parse(res.data.ingredients));
        })
        .catch((err) => {
          setError(err.response?.data.message || "Error Occured");
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [id, user?.token]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (error) {
    return <MessageCard error={error} />;
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl">{languageHelper("Add Purchase")}</h1>
          </div>
        </div>
        <div className="w-full">
          <form action="" className="w-full" onSubmit={handleSubmit}>
            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Reference No")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="0000002"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    defaultValue={formData.reference_no}
                    name="reference_no"
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Supplier")}
                </span>
                <div className="mb-2 pt-0 flex">
                  <Select
                    type="text"
                    className=" placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    placeholder={languageHelper("Select")}
                    options={suppliers}
                    defaultValue={{
                      value: formData.supplier_id,
                      label: formData.supplier?.name,
                    }}
                    name="supplier_id"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier_id: e.value,
                      })
                    }
                  />
                  <span
                    className="text-3xl mx-1 text-d-bg-text cursor-pointer font-bold"
                    onClick={() => setShowModal(true)}
                  >
                    +
                  </span>
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    placeholder={languageHelper("Date")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={formData.date}
                    name="date"
                    onChange={handleChange}
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Ingredients")}
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    placeholder={languageHelper("Select")}
                    options={ingredients}
                    onChange={handleIngredientSelect}
                    closeMenuOnSelect={false}
                  />
                </div>
              </label>

              <div className="w-full lg:w-1/2 mt-7">
                <button
                  className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                  type="button"
                  onClick={handleAddRow}
                >
                  {languageHelper("Add Row")}
                </button>
              </div>
            </div>

            <div>
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-xs py-1.5 px-2 text-start">{languageHelper("SN")}</th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Ingredient(Code)")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Unit Price")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Quantity/Amount")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Value(Qty)")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">{languageHelper("Price")}</th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Vat Percentage")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">
                      {languageHelper("Tax Method")}
                    </th>
                    <th className="text-xs py-1.5 px-2 text-start">{languageHelper("Vat(%)")}</th>
                    <th className="text-xs py-1.5 px-2 text-start">{languageHelper("Total")}</th>
                    <th className="text-xs py-1.5 px-2 text-start">{languageHelper("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedIngredient.map((ing, index) =>
                    ing.isNew ? (
                      <tr key={index} className="text-xs">
                        <td className="px-2 py-1">{index + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            className="border rounded px-1 py-0.5"
                            placeholder={languageHelper("Name")}
                            name="name"
                            value={ing.name}
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            placeholder={languageHelper("Unit Price")}
                            value={ing.price}
                            name="price"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                            className="border w-1/2 px-1 py-0.5 rounded"
                          />{" "}
                          {languageHelper("SR")}
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            placeholder={languageHelper("Qty/Amount")}
                            value={ing.qty}
                            name="qty"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                            className="border w-20 px-1 py-0.5 rounded"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <select
                            className="border px-1 py-0.5 rounded"
                            id=""
                            // value={ing.unit_id}
                            name="unit_id"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                          >
                            <option value="" selected disabled>{languageHelper("Select")}</option>
                            {units.map((unit, index) => (
                              <option
                                value={unit.id}
                                key={index}
                              >{`${unit.unit_name}(${unit.unit_value})`}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          {isNaN(
                            (parseFloat(ing.price) -
                              (ing.tax_method.toLowerCase() === "inclusive"
                                ? parseFloat(ing.vat)
                                : 0)) *
                              parseInt(ing.qty)
                          )
                            ? 0
                            : (
                                (parseFloat(ing.price) -
                                  (ing.tax_method.toLowerCase() === "inclusive"
                                    ? parseFloat(ing.vat)
                                    : 0)) *
                                parseInt(ing.qty)
                              ).toFixed(2)}{" "}
                          {languageHelper("SR")}
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            placeholder={languageHelper("Vat")}
                            className="border w-1/2 px-1 py-0.5 rounded"
                            value={ing.vat_percentage}
                            name="vat_percentage"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                          />
                        </td>
                        <td className="px-2 py-1">
                          <select
                            className="border px-1 py-0.5 rounded"
                            id=""
                            value={ing.tax_method}
                            name="tax_method"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                          >
                            <option value="" disabled selected>{languageHelper("Select")}</option>
                            <option value="No Tax" >
                              {languageHelper("No Tax")}
                            </option>
                            <option value="Inclusive">{languageHelper("Inclusive")}</option>
                            <option value="Exclusive">{languageHelper("Exclusive")}</option>
                          </select>
                        </td>
                        <td className="px-2 py-1">{ing.vat} SR</td>
                        <td className="px-2 py-1">{ing.total || 0.0} SR</td>
                        <td className="px-2 py-1">
                          <button
                            onClick={() => handleDelete(index)}
                            type="button"
                            className="border border-d-bg text-d-bg-text size-6 grid place-items-center rounded-full"
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={index} className="text-xs">
                        <td className="px-2 py-1">{index + 1}</td>
                        <td className="px-2 py-1">{`${ing.name} (${ing.code || ""})`}</td>
                        <td className="px-2 py-1">{ing.price} SR</td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            placeholder={languageHelper("Qty/Amount")}
                            value={ing.qty}
                            name="qty"
                            onChange={(e) =>
                              handleIngredientValueChange(e, index)
                            }
                            className="border w-20 px-1 py-0.5 rounded"
                          />
                        </td>
                        <td className="px-2 py-1">{`${ing.unit}(${ing.unitValue})`}</td>
                        <td className="px-2 py-1">
                          {isNaN(
                            (parseFloat(ing.price) -
                              (ing.tax_method.toLowerCase() === "inclusive"
                                ? parseFloat(ing.vat)
                                : 0)) *
                              parseInt(ing.qty)
                          )
                            ? 0
                            : (
                                (parseFloat(ing.price) -
                                  (ing.tax_method.toLowerCase() === "inclusive"
                                    ? parseFloat(ing.vat)
                                    : 0)) *
                                parseInt(ing.qty)
                              ).toFixed(2)}{" "}
                          {languageHelper("SR")}
                        </td>
                        <td className="px-2 py-1">{ing.vat_percentage}</td>
                        <td className="px-2 py-1">{ing.tax_method}</td>
                        <td className="px-2 py-1">{ing.vat} SR</td>
                        <td className="px-2 py-1">{ing.total || 0.0} SR</td>
                        <td className="px-2 py-1">
                          <button
                            onClick={() => handleDelete(index)}
                            type="button"
                            className="border border-d-bg text-d-bg-text size-6 grid place-items-center rounded-full"
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col lg:flex-col gap-x-8 gap-y-2 w-full items-end   ">
              <label htmlFor="" className="w-full lg:w-1/4">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("G. Total")}
                </span>
                <div className="mb-2 pt-0 flex space-y-1">
                  <input
                    type="text"
                    placeholder=""
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    value={
                      !isNaN(formData.grand_total) ? formData.grand_total : 0
                    }
                    name="granddgrand_total"
                    onChange={handleChange}
                    disabled
                  />
                  <p className="text-xs mt-3 ml-1">{languageHelper("S.R")}</p>
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/4">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Paid")}
                </span>
                <div className="mb-2 pt-0 flex">
                  <input
                    type="text"
                    placeholder=""
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={formData.paid}
                    name="paid"
                    onChange={handleChange}
                  />
                  <p className="text-xs mt-3 ml-1">{languageHelper("S.R")}</p>
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/4">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Due")}
                </span>
                <div className="mb-2 pt-0 flex">
                  <input
                    type="text"
                    placeholder=""
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={!isNaN(formData.due) ? formData.due : 0}
                    name="due"
                    onChange={handleChange}
                    disabled
                  />
                  <p className="text-xs mt-3 ml-1">{languageHelper("S.R")}</p>
                </div>
              </label>
              <label htmlFor="" className="w-full lg:w-1/4">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Payment Method")}
                </span>
                <div className="mb-2 pt-0 flex">
                  <Select
                    type="text"
                    placeholder={languageHelper("Select")}
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    options={paymentMethods}
                    defaultValue={{
                      value: formData.payment_method_id?.id,
                      label: formData.paymentMethod?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_method_id: e.value,
                      })
                    }
                  />
                  <p className="text-xs mt-3 ml-1">{languageHelper("S.R")}</p>
                </div>
              </label>
            </div>
            <div className="flex">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-d-bg-text text-sm mb-2">
                  {languageHelper("Note")}
                </span>
                <textarea
                  placeholder={languageHelper("Note")}
                  className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  value={formData.note}
                  name="note"
                  onChange={handleChange}
                />
              </label>
            </div>
            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="/assets/gif/button_loader.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit")}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back")}
              </button>
            </div>
          </form>
        </div>

        {showModal && (
          <AddSupplierModal
            setShowModal={setShowModal}
            setSuppliers={setSuppliers}
          />
        )}
      </div>
    </main>
  );
}

export default AddPurchasesForm;
